import { useState } from "react";
import { Compass, Loader2, AlertTriangle, Ban } from "lucide-react";
import { getBestChannel } from "../lib/api";

const CONF_CLS = {
    high: "text-[#f5f5f0] border-[#f5f5f0]",
    medium: "text-[#ffb800] border-[#ffb800]",
    low: "text-[#8a8a8a] border-[#262626]",
};

export const BestChannelStrip = ({ clipId }) => {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(false);

    const run = async () => {
        setLoading(true);
        setErr(null);
        try {
            const r = await getBestChannel(clipId);
            setData(r);
        } catch (e) {
            // Do not block library rendering — just surface a soft error.
            setErr(e?.response?.data?.detail || "Routing check failed");
        } finally {
            setLoading(false);
        }
    };

    if (!data && !loading && !err) {
        return (
            <div
                data-testid={`best-channel-strip-${clipId}`}
                className="flex items-center gap-3 px-5 py-2 bg-[#0a0a0a] border-t border-[#262626]"
            >
                <span className="d8-label">Routing</span>
                <button
                    data-testid={`best-channel-check-${clipId}`}
                    onClick={run}
                    className="font-mono-d8 text-[11px] uppercase tracking-widest text-[#f5f5f0] border border-[#262626] hover:border-[#ff2a2a] hover:text-[#ff2a2a] px-2 py-1 transition-colors inline-flex items-center gap-1.5"
                >
                    <Compass className="w-3 h-3" /> Check best channel
                </button>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center gap-3 px-5 py-2 bg-[#0a0a0a] border-t border-[#262626]">
                <span className="d8-label">Routing</span>
                <span className="font-mono-d8 text-[11px] text-[#8a8a8a] inline-flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> computing
                </span>
            </div>
        );
    }

    if (err) {
        return (
            <div
                data-testid={`best-channel-error-${clipId}`}
                className="flex items-center gap-3 px-5 py-2 bg-[#0a0a0a] border-t border-[#262626]"
            >
                <span className="d8-label">Routing</span>
                <span className="font-mono-d8 text-[11px] text-[#8a8a8a]">routing unavailable</span>
                <button onClick={run} className="font-mono-d8 text-[10px] uppercase tracking-widest text-[#f5f5f0] underline">retry</button>
            </div>
        );
    }

    const state = data.data_state;
    if (state === "empty") {
        return (
            <div
                data-testid={`best-channel-empty-${clipId}`}
                className="flex items-center gap-3 px-5 py-2 bg-[#0a0a0a] border-t border-[#262626]"
            >
                <span className="d8-label">Routing</span>
                <span className="font-mono-d8 text-[11px] text-[#8a8a8a]">No channels yet — add one in Ops.</span>
            </div>
        );
    }

    const rec = data.recommended_channel;
    const insufficient = state === "insufficient";

    return (
        <div
            data-testid={`best-channel-result-${clipId}`}
            className="px-5 py-3 bg-[#0a0a0a] border-t border-[#262626] space-y-2"
        >
            <div className="flex items-center gap-3 flex-wrap">
                <span className="d8-label">Best channel</span>
                {rec ? (
                    <>
                        <span className="font-mono-d8 text-xs text-[#f5f5f0]">
                            {rec.channel_name} <span className="text-[#8a8a8a]">/ {rec.platform}{rec.handle ? ` · ${rec.handle}` : ""}</span>
                        </span>
                        <span
                            data-testid={`best-channel-confidence-${clipId}`}
                            className={`font-mono-d8 text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${CONF_CLS[rec.confidence] || CONF_CLS.low}`}
                        >
                            {insufficient ? "INSUFFICIENT" : rec.confidence}
                        </span>
                        <span className="font-mono-d8 text-[10px] text-[#8a8a8a]">score {(rec.score || 0).toFixed(2)} · n={data.sample_size}</span>
                        <button
                            data-testid={`best-channel-refresh-${clipId}`}
                            onClick={run}
                            className="font-mono-d8 text-[10px] uppercase tracking-widest text-[#8a8a8a] hover:text-[#f5f5f0] underline ml-auto"
                        >
                            refresh
                        </button>
                    </>
                ) : (
                    <span className="font-mono-d8 text-[11px] text-[#8a8a8a]">Not enough data yet — ingest more events.</span>
                )}
            </div>
            {data.explanation && (
                <p className="font-mono-d8 text-[11px] text-[#f5f5f0] leading-snug">{data.explanation}</p>
            )}
            {(data.avoid_channels || []).length > 0 && (
                <div className="flex flex-wrap items-start gap-1.5">
                    <span className="d8-label inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-[#ffb800]" /> Avoid</span>
                    {data.avoid_channels.slice(0, 3).map((a) => (
                        <span
                            key={a.channel_id}
                            data-testid={`best-channel-avoid-${a.channel_id}`}
                            title={a.reason}
                            className="font-mono-d8 text-[10px] uppercase tracking-widest text-[#ff2a2a] border border-[#ff2a2a] px-1.5 py-0.5 inline-flex items-center gap-1"
                        >
                            <Ban className="w-3 h-3" /> {a.channel_name} ({a.platform})
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BestChannelStrip;
